import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactDOM from 'react-dom/client';
import { useAuth } from '../AuthContext';
import { useSidebar } from '../../../context/SidebarContext';
import ConfirmModal from '../../common/ConfirmModal';
import InspectionPrintSheet from './InspectionPrintSheet';
import { generateInspectionPDFBlob } from './inspectionPDFGenerator';
import { fetchCompanyInfo } from '../../../utils/companyInfo';
const { rootUrl } = require('../../../config/config');

const STATUS_OPTIONS = [
    { value: 'passed', label: '✓ Pass', bg: 'bg-green-500', activeBg: 'bg-green-600', textColor: 'text-white' },
    { value: 'failed', label: '✗ Fail', bg: 'bg-red-500', activeBg: 'bg-red-600', textColor: 'text-white' },
    { value: 'needs_attention', label: '⚠ Attn', bg: 'bg-amber-400', activeBg: 'bg-amber-500', textColor: 'text-white' },
    { value: 'not_checked', label: '— Skip', bg: 'bg-gray-300', activeBg: 'bg-gray-400', textColor: 'text-gray-700' },
];

function useDebounce(fn, delay) {
    const timer = useRef(null);
    return useCallback((...args) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => fn(...args), delay);
    }, [fn, delay]);
}

const InspectionConduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isSidebarOpen, isCollapsed } = useSidebar();
    const token = localStorage.getItem('authToken') || 'default-token';
    const authHeaders = { Authorization: `Bearer ${token}` };

    const [inspection, setInspection] = useState(null);
    const [checkItems, setCheckItems] = useState([]);
    const [overallNotes, setOverallNotes] = useState('');
    const [recommendations, setRecommendations] = useState('');
    const [generalPhotos, setGeneralPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saveProgressState, setSaveProgressState] = useState('idle'); // idle | saving | saved | failed
    const [completing, setCompleting] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmError, setConfirmError] = useState('');
    const [photoUploading, setPhotoUploading] = useState({});
    const [openCategories, setOpenCategories] = useState({});

    // Load inspection
    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${rootUrl}/cars/inspections/internal/${id}`, { headers: authHeaders });
                const insp = res.data;
                setInspection(insp);
                setCheckItems(insp.check_items || []);
                setOverallNotes(insp.overall_notes || '');
                setRecommendations(insp.recommendations || '');
                setGeneralPhotos(insp.metadata?.photos || []);
                // Default: all categories expanded
                const cats = {};
                (insp.check_items || []).forEach(item => { cats[item.category || 'Other'] = true; });
                setOpenCategories(cats);
            } catch {
                setError('Failed to load inspection.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    // Save check items (auto-save path — silent)
    const saveCheckItems = useCallback(async (items) => {
        try {
            await axios.post(
                `${rootUrl}/cars/inspections/internal/${id}/checks`,
                { check_items: items.map(i => ({ check_item_id: i.check_item_id, status: i.status, notes: i.notes || null, photo_link: i.photo_link || null })), edit_by: user?.username || 'system' },
                { headers: authHeaders }
            );
        } catch { /* silent — manual save gives explicit feedback */ }
    }, [id, user]);

    const debouncedSaveCheckItems = useDebounce(saveCheckItems, 800);

    // Save notes/recommendations (auto-save path — silent)
    const saveNotes = useCallback(async (fields) => {
        try {
            await axios.put(
                `${rootUrl}/cars/inspections/internal/${id}`,
                { ...fields, edit_by: user?.username || 'system' },
                { headers: authHeaders }
            );
        } catch { /* silent — manual save gives explicit feedback */ }
    }, [id, user]);

    const debouncedSaveNotes = useDebounce(saveNotes, 1000);

    // Update a single check item status
    const handleStatusChange = (checkItemId, newStatus) => {
        const updated = checkItems.map(item =>
            item.check_item_id === checkItemId
                ? { ...item, status: newStatus, notes: (newStatus === 'passed' || newStatus === 'not_checked') ? '' : item.notes }
                : item
        );
        setCheckItems(updated);
        debouncedSaveCheckItems(updated);
    };

    // Update a single check item notes
    const handleNotesChange = (checkItemId, notes) => {
        const updated = checkItems.map(item =>
            item.check_item_id === checkItemId ? { ...item, notes } : item
        );
        setCheckItems(updated);
        debouncedSaveCheckItems(updated);
    };

    // Upload photo for a check item
    const handleItemPhotoChange = async (checkItemId, file) => {
        if (!file || !inspection?.metadata?.drive_folder_id) return;
        setPhotoUploading(prev => ({ ...prev, [checkItemId]: true }));
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('folder_id', inspection.metadata.drive_folder_id);
            const res = await axios.post(`${rootUrl}/api/google-drive/upload`, formData, {
                headers: authHeaders,
            });
            const photoUrl = res.data.data?.webViewLink;
            const updated = checkItems.map(item =>
                item.check_item_id === checkItemId ? { ...item, photo_link: photoUrl } : item
            );
            setCheckItems(updated);
            // Send full item state to avoid nulling status/notes
            const changedItem = updated.find(i => i.check_item_id === checkItemId);
            await axios.post(
                `${rootUrl}/cars/inspections/internal/${id}/checks`,
                { check_items: [{ check_item_id: changedItem.check_item_id, status: changedItem.status, notes: changedItem.notes || null, photo_link: changedItem.photo_link }], edit_by: user?.username || 'system' },
                { headers: authHeaders }
            );
        } catch { setError('Photo upload failed. Please try again.'); }
        finally { setPhotoUploading(prev => ({ ...prev, [checkItemId]: false })); }
    };

    // Upload general photo
    const handleGeneralPhotoChange = async (file) => {
        if (!file || !inspection?.metadata?.drive_folder_id) return;
        setPhotoUploading(prev => ({ ...prev, general: true }));
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('folder_id', inspection.metadata.drive_folder_id);
            const res = await axios.post(`${rootUrl}/api/google-drive/upload`, formData, {
                headers: authHeaders,
            });
            const uploaded = res.data.data;
            const newPhoto = { name: file.name, drive_file_id: uploaded.id, url: uploaded.webViewLink, uploaded_at: new Date().toISOString() };
            const updatedPhotos = [...generalPhotos, newPhoto];
            setGeneralPhotos(updatedPhotos);
            await axios.put(
                `${rootUrl}/cars/inspections/internal/${id}`,
                { metadata: { ...inspection.metadata, photos: updatedPhotos }, edit_by: user?.username || 'system' },
                { headers: authHeaders }
            );
        } catch { setError('Photo upload failed. Please try again.'); }
        finally { setPhotoUploading(prev => ({ ...prev, general: false })); }
    };

    // Save progress manually — provides inline button feedback
    const handleSaveProgress = async () => {
        setSaveProgressState('saving');
        try {
            await axios.post(
                `${rootUrl}/cars/inspections/internal/${id}/checks`,
                { check_items: checkItems.map(i => ({ check_item_id: i.check_item_id, status: i.status, notes: i.notes || null, photo_link: i.photo_link || null })), edit_by: user?.username || 'system' },
                { headers: authHeaders }
            );
            await axios.put(
                `${rootUrl}/cars/inspections/internal/${id}`,
                { overall_notes: overallNotes, recommendations, edit_by: user?.username || 'system' },
                { headers: authHeaders }
            );
            setSaveProgressState('saved');
            setTimeout(() => setSaveProgressState('idle'), 2000);
        } catch {
            setSaveProgressState('failed');
            setTimeout(() => setSaveProgressState('idle'), 2000);
        }
    };

    // Complete inspection — just marks as done; PDF is generated on demand separately
    const handleComplete = async () => {
        setCompleting(true);
        setConfirmError('');
        try {
            await axios.post(
                `${rootUrl}/cars/inspections/internal/${id}/complete`,
                { edit_by: user?.username || 'system' },
                { headers: authHeaders }
            );
            setShowConfirm(false);
            navigate('/inspections/internal', { state: { success: 'Inspection completed successfully.' } });
        } catch (err) {
            setConfirmError(err.response?.data?.message || err.message || 'Failed to complete inspection.');
        } finally {
            setCompleting(false);
        }
    };

    // Generate and download PDF on demand
    const handleDownloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const companyInfo = await fetchCompanyInfo(token);
            const fullInspection = { ...inspection, check_items: checkItems, overall_notes: overallNotes, recommendations, metadata: { ...(inspection.metadata || {}), photos: generalPhotos } };
            const tempDiv = document.createElement('div');
            tempDiv.id = 'inspection-print-sheet';
            tempDiv.style.cssText = 'position:absolute;left:-9999px;width:210mm;';
            document.body.appendChild(tempDiv);
            const root = ReactDOM.createRoot(tempDiv);
            root.render(<InspectionPrintSheet inspection={fullInspection} companyInfo={companyInfo} />);
            await new Promise(resolve => setTimeout(resolve, 1000));
            let blob;
            try {
                blob = await generateInspectionPDFBlob(fullInspection);
            } finally {
                root.unmount();
                document.body.removeChild(tempDiv);
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `inspection_report_${id}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            setError('Failed to generate PDF. Please try again.');
        } finally {
            setDownloadingPdf(false);
        }
    };

    // Group check items by category
    const grouped = checkItems.reduce((acc, item) => {
        const cat = item.category || 'Other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
    }, {});

    if (loading) return <div className="flex items-center justify-center min-h-screen text-gray-500">Loading inspection...</div>;
    if (!inspection) return <div className="flex items-center justify-center min-h-screen text-red-500">{error || 'Inspection not found.'}</div>;

    const isCompleted = inspection.inspection_status === 'completed';

    return (
        <div className="min-h-screen bg-gray-50 pb-24">
            {/* Sticky top bar */}
            <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
                <button onClick={() => navigate('/inspections/internal')} className="text-gray-600 font-medium flex items-center gap-1">
                    ← Back
                </button>
                <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900">{inspection.plate_no}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${isCompleted ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                        {isCompleted ? 'Completed' : 'In Progress'}
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    {isCompleted && (
                        <button
                            onClick={handleDownloadPdf}
                            disabled={downloadingPdf}
                            className="text-sm font-medium text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-lg disabled:opacity-50"
                        >
                            {downloadingPdf ? 'Generating…' : '↓ PDF'}
                        </button>
                    )}
                    {inspection.metadata?.drive_folder_link && (
                        <a href={inspection.metadata.drive_folder_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm">
                            Drive ↗
                        </a>
                    )}
                </div>
            </div>

            {error && <div className="mx-4 mt-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">{error}</div>}

            <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
                {/* Inspection meta */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 text-sm text-gray-600 space-y-1">
                    <div><span className="font-medium text-gray-800">Inspector:</span> {inspection.inspector_name}</div>
                    <div><span className="font-medium text-gray-800">Date:</span> {inspection.inspection_date}</div>
                    <div>
                        <span className="font-medium text-gray-800">Mileage:</span>{' '}
                        {inspection.mileage?.toLocaleString()} km
                        {inspection.distance_since_last != null && (
                            <span className="text-gray-400 ml-2">(+{inspection.distance_since_last.toLocaleString()} km since last)</span>
                        )}
                    </div>
                </div>

                {/* Check items by category */}
                {Object.entries(grouped).map(([category, items]) => (
                    <div key={category} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <button
                            className="w-full flex justify-between items-center px-4 py-3 bg-gray-50 text-sm font-semibold text-gray-800 border-b border-gray-200"
                            onClick={() => setOpenCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                        >
                            <span>{category}</span>
                            <span>{openCategories[category] ? '▲' : '▼'}</span>
                        </button>
                        {openCategories[category] && (
                            <div className="divide-y divide-gray-100">
                                {items.map(item => {
                                    const requiresNotes = item.status === 'failed' || item.status === 'needs_attention';
                                    return (
                                        <div key={item.check_item_id} className="p-4 space-y-3">
                                            <div>
                                                <div className="font-semibold text-gray-900 text-sm">{item.item_name}</div>
                                                <div className="text-xs text-gray-400">{item.item_code}</div>
                                            </div>
                                            {/* Status buttons */}
                                            <div className="flex gap-2 flex-wrap">
                                                {STATUS_OPTIONS.map(opt => (
                                                    <button
                                                        key={opt.value}
                                                        disabled={isCompleted}
                                                        onClick={() => handleStatusChange(item.check_item_id, opt.value)}
                                                        className={`py-2 px-3 rounded text-xs font-medium min-h-[44px] transition-opacity ${opt.textColor} ${item.status === opt.value ? opt.activeBg : `${opt.bg} opacity-50`} ${isCompleted ? 'cursor-not-allowed' : ''}`}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                            {/* Notes — only for failed / needs_attention */}
                                            {requiresNotes && (
                                                <textarea
                                                    disabled={isCompleted}
                                                    placeholder="Notes (required) *"
                                                    value={item.notes || ''}
                                                    onChange={e => handleNotesChange(item.check_item_id, e.target.value)}
                                                    className="w-full border border-gray-300 rounded p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                                    rows={2}
                                                />
                                            )}
                                            {/* Photo */}
                                            {!isCompleted && (
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded text-xs font-medium min-h-[44px] flex items-center gap-1">
                                                        {photoUploading[item.check_item_id] ? 'Uploading…' : '📷 Camera'}
                                                        <input type="file" accept="image/*" capture="environment" className="hidden" disabled={photoUploading[item.check_item_id]} onChange={e => e.target.files[0] && handleItemPhotoChange(item.check_item_id, e.target.files[0])} />
                                                    </label>
                                                    {!photoUploading[item.check_item_id] && (
                                                        <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded text-xs font-medium min-h-[44px] flex items-center gap-1">
                                                            🖼 Gallery
                                                            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handleItemPhotoChange(item.check_item_id, e.target.files[0])} />
                                                        </label>
                                                    )}
                                                    {item.photo_link && (
                                                        <a href={item.photo_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs underline">
                                                            View ↗
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                            {isCompleted && item.photo_link && (
                                                <a href={item.photo_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs underline">View photo ↗</a>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ))}

                {/* Overall Notes */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
                    <label className="block text-sm font-semibold text-gray-800">Overall Notes</label>
                    <textarea
                        disabled={isCompleted}
                        value={overallNotes}
                        onChange={e => { setOverallNotes(e.target.value); debouncedSaveNotes({ overall_notes: e.target.value, recommendations }); }}
                        placeholder="General observations about this inspection…"
                        className="w-full border border-gray-300 rounded p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        rows={3}
                    />
                </div>

                {/* Recommendations */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
                    <label className="block text-sm font-semibold text-gray-800">Recommendations</label>
                    <textarea
                        disabled={isCompleted}
                        value={recommendations}
                        onChange={e => { setRecommendations(e.target.value); debouncedSaveNotes({ overall_notes: overallNotes, recommendations: e.target.value }); }}
                        placeholder="Follow-up actions recommended…"
                        className="w-full border border-gray-300 rounded p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        rows={3}
                    />
                </div>

                {/* General Photos */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                    <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-800">General Photos</span>
                        {!isCompleted && (
                            <div className="flex gap-2">
                                <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded text-xs font-medium min-h-[44px] flex items-center gap-1">
                                    {photoUploading.general ? 'Uploading…' : '📷 Camera'}
                                    <input type="file" accept="image/*" capture="environment" className="hidden" disabled={photoUploading.general} onChange={e => e.target.files[0] && handleGeneralPhotoChange(e.target.files[0])} />
                                </label>
                                {!photoUploading.general && (
                                    <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded text-xs font-medium min-h-[44px] flex items-center gap-1">
                                        🖼 Gallery
                                        <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handleGeneralPhotoChange(e.target.files[0])} />
                                    </label>
                                )}
                            </div>
                        )}
                    </div>
                    {generalPhotos.length > 0 ? (
                        <div className="grid grid-cols-3 gap-2">
                            {generalPhotos.map((photo, idx) => (
                                <a key={idx} href={photo.url} target="_blank" rel="noopener noreferrer">
                                    <div className="border border-gray-200 rounded overflow-hidden aspect-square bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                                        📸 {photo.name || `Photo ${idx + 1}`}
                                    </div>
                                </a>
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400">No photos yet.</p>
                    )}
                </div>
            </div>

            {/* Sticky bottom action bar */}
            {!isCompleted && (
                <div className={`fixed bottom-0 right-0 z-30 bg-white border-t border-gray-200 shadow-lg transition-all duration-300 ${
                    !isSidebarOpen ? 'left-0' : isCollapsed ? 'left-12 md:left-14' : 'left-52 lg:left-56'
                }`}>
                    <div className="max-w-2xl mx-auto px-4 py-3 flex gap-3">
                        <button
                            onClick={handleSaveProgress}
                            disabled={saveProgressState === 'saving'}
                            className={`flex-1 py-3 rounded-lg text-sm font-medium border transition-colors ${
                                saveProgressState === 'saved' ? 'bg-green-50 text-green-700 border-green-300' :
                                saveProgressState === 'failed' ? 'bg-red-50 text-red-700 border-red-300' :
                                'bg-gray-100 text-gray-800 border-gray-300'
                            } disabled:opacity-60`}
                        >
                            {saveProgressState === 'saving' ? 'Saving…' :
                             saveProgressState === 'saved' ? '✓ Saved' :
                             saveProgressState === 'failed' ? '✗ Save Failed' :
                             'Save Progress'}
                        </button>
                        <button
                            onClick={() => setShowConfirm(true)}
                            className="flex-1 bg-green-500 text-white py-3 rounded-lg text-sm font-bold"
                        >
                            ✓ Complete
                        </button>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showConfirm}
                title="Complete Inspection"
                description="This will mark the inspection as completed. This cannot be undone. You can download the PDF report afterwards."
                onCancel={() => { setShowConfirm(false); setConfirmError(''); }}
                onConfirm={handleComplete}
                confirmText={completing ? 'Completing…' : 'Complete Inspection'}
                cancelText="Cancel"
                loading={completing}
                error={confirmError}
                variant="primary"
            />
        </div>
    );
};

export default InspectionConduct;
